import { ProjectRenderer } from '../ProjectRenderer';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { RenderUtils } from '../utils/RenderUtils';
import {
	MeshStandardMaterial,
	ConeGeometry,
	Vector3,
	Group,
	Mesh,
	Box3,
} from 'three';

const material = new LineMaterial({
		color: 0x2e2e2e,
		linewidth: 2,
	}),
	arrowHeadMat = new MeshStandardMaterial({ color: material.color });

const coneGeometry = new ConeGeometry(0.015, 0.075),
	halfConeHeight = coneGeometry.parameters.height;

export class BoxMeasures {
	private dimensions = {
		width: 0,
		height: 0,
		depth: 0,
	};
	private _visible = false;
	private points: {
		x: [Vector3, Vector3];
		y: [Vector3, Vector3];
		z: [Vector3, Vector3];
	} = {
		x: [new Vector3(), new Vector3()],
		y: [new Vector3(), new Vector3()],
		z: [new Vector3(), new Vector3()],
	};
	private lines = {
		x: new Group(),
		y: new Group(),
		z: new Group(),
	};
	static readonly ARROW_TO_BOX_OFFSET = 0.1;

	constructor(private renderer: ProjectRenderer) {}

	get visible() {
		return this._visible;
	}

	set visible(value: boolean) {
		this._visible = value;

		let axis: keyof typeof this.lines;
		for (axis in this.lines) {
			this.lines[axis].visible = this.visible;
		}
	}

	private createArrowhead(position: Vector3, direction: Vector3) {
		const cone = new Mesh(coneGeometry, arrowHeadMat);

		const offset = direction
			.clone()
			.normalize()
			.multiplyScalar(halfConeHeight / 2);
		cone.position.copy(position).sub(offset);
		cone.quaternion.setFromUnitVectors(
			new Vector3(0, 1, 0),
			direction.clone().normalize()
		);

		return cone;
	}

	private setDimensionsFromBox(box: Box3) {
		this.dimensions.width = Math.abs(box.max.x - box.min.x);
		this.dimensions.height = Math.abs(box.max.y - box.min.y);
		this.dimensions.depth = Math.abs(box.max.z - box.min.z);
	}

	private setPointsFromBox(box: Box3) {
		this.setDimensionsFromBox(box);

		this.points.x[0].set(
			box.min.x,
			box.min.y,
			box.max.z + BoxMeasures.ARROW_TO_BOX_OFFSET
		);
		this.points.x[1].set(
			box.max.x,
			box.min.y,
			box.max.z + BoxMeasures.ARROW_TO_BOX_OFFSET
		);

		this.points.y[0].set(
			box.min.x - BoxMeasures.ARROW_TO_BOX_OFFSET,
			box.min.y,
			box.min.z + box.max.z / 2
		);
		this.points.y[1].set(
			box.min.x - BoxMeasures.ARROW_TO_BOX_OFFSET,
			box.max.y,
			box.min.z + box.max.z / 2
		);

		this.points.z[0].set(
			box.max.x + BoxMeasures.ARROW_TO_BOX_OFFSET,
			box.min.y,
			box.max.z
		);
		this.points.z[1].set(
			box.max.x + BoxMeasures.ARROW_TO_BOX_OFFSET,
			box.min.y,
			box.min.z
		);
	}

	private drawArrowLine(axis: keyof typeof this.points) {
		const start = this.points[axis][1],
			end = this.points[axis][0];
		const direction = end.clone().sub(start);

		const shortenedLine = [
			start.clone().add(direction.clone().normalize().multiplyScalar(0.05)),
			end.clone().sub(direction.clone().normalize().multiplyScalar(0.05)),
		];

		const geo = new LineGeometry();

		const line = new Line2(geo.setFromPoints(shortenedLine), material);
		this.renderer.scene.add(line);

		const arrowStart = this.createArrowhead(start, direction.clone().negate());
		const arrowEnd = this.createArrowhead(end, direction);

		this.lines[axis].add(line, arrowStart, arrowEnd);
		this.lines[axis].layers.enableAll();
		this.renderer.scene.add(this.lines[axis]);

		this.createLabel(axis);
	}

	private createLabel(axis: keyof typeof this.points) {
		const start = this.points[axis][1],
			end = this.points[axis][0];

		const center = start.clone().add(end).divideScalar(2);

		const div = document.createElement('div');
		div.className = 'renderer-label';

		let length = 0;
		switch (axis) {
			case 'x':
				length = this.dimensions.width;
				break;
			case 'y':
				length = this.dimensions.height;
				break;
			case 'z':
				length = this.dimensions.depth;
				break;
		}

		div.textContent = `${Math.round(length * 100)} cm`;

		const lengthLabel = new CSS2DObject(div);
		lengthLabel.position.copy(center);
		switch (axis) {
			case 'x':
				lengthLabel.position.z += 0.05;
				break;
			case 'y':
				lengthLabel.position.z += 0.05;
				break;
			case 'z':
				lengthLabel.position.x += 0.05;
				break;
		}

		lengthLabel.center.set(0, 0);
		this.lines[axis].add(lengthLabel);
		lengthLabel.layers.set(0);
	}

	draw() {
		const box = new Box3().setFromObject(this.renderer.collection);

		this.dispose();
		this.setPointsFromBox(box);

		for (const axis of Object.keys(this.points)) {
			this.drawArrowLine(axis as keyof typeof this.points);
		}

		this.visible = this.visible;
	}

	toggleVisibility() {
		this.visible = !this.visible;
	}

	dispose() {
		let axis: keyof typeof this.lines;
		for (axis in this.lines) {
			RenderUtils.clearGroup(this.lines[axis]);
		}
	}
}
